import { PublicPlan } from '@wix/ambassador-pricing-plans-v2-plan/types';
import { Image, Plan as PlanV3 } from '@wix/ambassador-pricing-plans-v3-plan/types';
import { PlanWidgetRole } from '@wix/pricing-plans-common/blocks';
import { TPA_EXPERIMENTS } from '@wix/pricing-plans-common/experiments';
import { getPricingPlansPublicAPI } from '@wix/pricing-plans-tpa-api';
import { isPlanV3 } from '@wix/pricing-plans-utils';
import { CoverImage, getCoverImage } from '@wix/pricing-plans-utils/client-data';
import { TFunction } from '@wix/yoshi-flow-editor';
import { FormattedPriceData, getFormattedPriceData } from '../../hooks';
import { Analytics } from '../../services/analytics';
import { resolveLocale } from '../../utils';
import { generateUuid } from '../../utils/generate-uuid';
import { getFormattedAdditionalFees as getFormattedAdditionalFeesV2 } from '../../utils/get-formatted-additional-fees';
import { getFreeTrialDaysLabel as getFreeTrialDaysLabelV2 } from '../../utils/getFreeTrialDaysLabel';
import { getPeriodLabel as getPeriodLabelV2 } from '../../utils/getPeriodLabel';
import { getPlanDuration as getPlanDurationV2 } from '../../utils/getPlanValidityCycle';
import { isMultiPageApp } from '../../utils/isMultiPageApp';
import {
  getVariantFreeTrialDaysLabel,
  getVariantAdditionalFees,
  getVariantDuration,
  getVariantPeriodLabel,
} from '../../utils/plan-v3-utils';
import { PricingData, PricingElementOptions } from '../Pricing/viewer.controller';
import { displayLoader } from './loader/display-loader';
import { mapPlanV3ToPublicPlan } from './map-plan-v3-to-public-plan';
import model from './model';

export type CtaClickHandler = (plan: PublicPlan | PlanV3) => void;

export type PlanElementOptions = PricingElementOptions & {
  forceExpandFreeTrial?: boolean;
  defaultCoverImage?: CoverImage;
};

export default model.createController(({ $w, flowAPI }) => {
  let externalOnSelectHandler: CtaClickHandler | undefined;

  const updatePlanData = (plan: PublicPlan | PlanV3, elementOptions?: PlanElementOptions) => {
    const t = flowAPI.translations.t as TFunction;

    if (isPlanV3(plan)) {
      const altText = plan.image?.altText ?? '';
      $w(`#${PlanWidgetRole.Image}`).src = getImageSrc(
        planImageToCoverImage(plan.image) ?? elementOptions?.defaultCoverImage,
      );
      $w(`#${PlanWidgetRole.Image}`).alt = altText;
      $w(`#${PlanWidgetRole.Image}`).expand();

      setDescription(plan);

      const variant = plan.pricingVariants![0];
      const freeTrial = getVariantFreeTrialDaysLabel(variant, t);
      const duration = getVariantDuration(variant, t);
      let planDuration = freeTrial ? `${duration}\n${freeTrial}` : duration;
      if (!freeTrial && elementOptions?.forceExpandFreeTrial) {
        planDuration += '\n\n';
      }

      $w(`#${PlanWidgetRole.PlanName}`).text = plan.name!;
      $w(`#${PlanWidgetRole.PlanDuration}`).text = planDuration;

      const { price, currency, fullPrice } = getPriceDataV3(plan);
      const paymentFrequency = getVariantPeriodLabel(variant, t) ?? '';
      const setupFee = getSetupFeeV3(plan);
      $w(`#${PlanWidgetRole.PricingWidget}`).setPricing(
        {
          price,
          currency,
          paymentFrequency,
          setupFee,
        } satisfies PricingData,
        elementOptions,
      );
      setA11yAttributes(plan, fullPrice);

      $w(`#${PlanWidgetRole.PerksWidget}`).setPerks(plan.perks?.map((p) => p.description ?? '') ?? []);

      if (plan.perks?.length) {
        $w(`#${PlanWidgetRole.ContentDividerContainer}`).show();
        $w(`#${PlanWidgetRole.PerksWidget}`).expand();
      } else {
        $w(`#${PlanWidgetRole.ContentDividerContainer}`).hide();
        $w(`#${PlanWidgetRole.PerksWidget}`).collapse();
      }
    } else {
      const coverImage = getCoverImage((plan as PublicPlan).clientData);
      $w(`#${PlanWidgetRole.Image}`).src = getImageSrc(coverImage ?? elementOptions?.defaultCoverImage);
      $w(`#${PlanWidgetRole.Image}`).alt = coverImage?.altText ?? '';
      $w(`#${PlanWidgetRole.Image}`).expand();

      setDescription(plan);

      const freeTrial = getFreeTrialDaysLabelV2(plan, t);
      const duration = getPlanDurationV2(plan, t);
      let planDuration = freeTrial ? `${duration}\n${freeTrial}` : duration;
      if (!freeTrial && elementOptions?.forceExpandFreeTrial) {
        planDuration += '\n\n';
      }

      $w(`#${PlanWidgetRole.PlanName}`).text = plan.name!;
      $w(`#${PlanWidgetRole.PlanDuration}`).text = planDuration;

      const { price, currency, fullPrice } = getPriceDataV2(plan);
      const paymentFrequency = getPeriodLabelV2(plan.pricing?.subscription, t) ?? '';
      const setupFee = getSetupFeeV2(plan);
      $w(`#${PlanWidgetRole.PricingWidget}`).setPricing(
        {
          price,
          currency,
          paymentFrequency,
          setupFee,
        } satisfies PricingData,
        elementOptions,
      );
      setA11yAttributes(plan, fullPrice);

      $w(`#${PlanWidgetRole.PerksWidget}`).setPerks(plan.perks?.values?.map((v) => v) ?? []);

      if (plan.perks?.values?.length) {
        $w(`#${PlanWidgetRole.ContentDividerContainer}`).show();
        $w(`#${PlanWidgetRole.PerksWidget}`).expand();
      } else {
        $w(`#${PlanWidgetRole.ContentDividerContainer}`).hide();
        $w(`#${PlanWidgetRole.PerksWidget}`).collapse();
      }
    }
  };

  const setDescription = (plan: PublicPlan | PlanV3) => {
    if (plan.description) {
      $w(`#${PlanWidgetRole.Description}`).text = plan.description;
      $w(`#${PlanWidgetRole.Description}`).expand();
    } else {
      $w(`#${PlanWidgetRole.Description}`).text = '';
      $w(`#${PlanWidgetRole.Description}`).collapse();
    }
  };

  const setA11yAttributes = (plan: PublicPlan | PlanV3, formattedPrice: string) => {
    // @ts-expect-error
    $w(`#${PlanWidgetRole.PlanContainer}`).accessibility.ariaAttributes.label = plan.name;

    if ($w(`#${PlanWidgetRole.RibbonWidget}`).isVisible) {
      const ribbonTextElement = $w(`#${PlanWidgetRole.RibbonWidget}`).getTextElement();
      // @ts-expect-error
      $w(`#${PlanWidgetRole.PlanContainer}`).accessibility.ariaAttributes.describedBy = ribbonTextElement;
    }

    if ($w(`#${PlanWidgetRole.PricingWidget}`).isVisible && !flowAPI.environment.isEditor) {
      // @ts-expect-error
      $w(`#${PlanWidgetRole.PlanName}`).accessibility.screenReader.suffix = formattedPrice;
    }
  };

  const getPriceDataV3 = (plan: PlanV3): FormattedPriceData => {
    const pricing = plan.pricingVariants![0];
    const value = pricing?.pricingStrategies?.[0]?.flatRate?.amount;
    const currency = plan.currency;
    return getFormattedPriceData({
      createCurrencyFormatter: flowAPI.getCurrencyFormatter,
      locale: resolveLocale(flowAPI.controllerConfig.wixCodeApi),
      value,
      currency,
      hideTrailingFractionZeroes: true,
    });
  };

  const getPriceDataV2 = (plan: PublicPlan): FormattedPriceData => {
    const value = plan.pricing?.price?.value ?? undefined;
    const currency = plan.pricing?.price?.currency ?? undefined;
    return getFormattedPriceData({
      createCurrencyFormatter: flowAPI.getCurrencyFormatter,
      locale: resolveLocale(flowAPI.controllerConfig.wixCodeApi),
      value,
      currency,
      hideTrailingFractionZeroes: true,
    });
  };

  const getSetupFeeV3 = (plan: PlanV3): string => {
    return getVariantAdditionalFees({
      variant: plan.pricingVariants![0],
      currency: plan.currency,
      locale: resolveLocale(flowAPI.controllerConfig.wixCodeApi),
      createCurrencyFormatter: flowAPI.getCurrencyFormatter,
      hideTrailingFractionZeroes: true,
    })
      .map(({ amount, name }) => `+${amount} ${name}`)
      .join('\n');
  };

  const getSetupFeeV2 = (plan: PublicPlan): string => {
    return getFormattedAdditionalFeesV2({
      plan,
      locale: resolveLocale(flowAPI.controllerConfig.wixCodeApi),
      createCurrencyFormatter: flowAPI.getCurrencyFormatter,
      hideTrailingFractionZeroes: true,
    })
      .map(({ amount, name }) => `+${amount} ${name}`)
      .join('\n');
  };

  const reportPerformanceMark = (callback: (params: any) => void) => {
    if (typeof performance !== 'undefined') {
      performance.mark && setTimeout(() => performance.mark('pricing-plans-rendered'), 1000);
    }
    return callback;
  };

  const registerCtaHandler = async (plan: PublicPlan | PlanV3, widgetType: string, isOnEcom: boolean) => {
    const [api, isMPA] = await Promise.all([
      getPricingPlansPublicAPI(flowAPI.controllerConfig.wixCodeApi),
      isMultiPageApp(flowAPI.controllerConfig.wixCodeApi, flowAPI.controllerConfig.appParams),
    ]);
    const btn = $w(`#${PlanWidgetRole.Button}`);
    btn.onClick(
      reportPerformanceMark(async () => {
        if (typeof externalOnSelectHandler === 'function') {
          const veloHandlerPlan = isPlanV3(plan) ? mapPlanV3ToPublicPlan(plan) : plan;
          return externalOnSelectHandler(veloHandlerPlan);
        }

        const allowCheckoutInEditorPreview = flowAPI.experiments.enabled(
          TPA_EXPERIMENTS.ALLOW_CHECKOUT_IN_EDITOR_PREVIEW,
        );
        if (flowAPI.controllerConfig.wixCodeApi.window.viewMode !== 'Site' && !allowCheckoutInEditorPreview) {
          return flowAPI.openModal('AlertModal', { width: 504, height: 364 });
        }

        const analytics = new Analytics(flowAPI.controllerConfig.wixCodeApi.window);
        analytics.clickProduct(plan);
        const checkoutFlowId = generateUuid();

        if (isMPA) {
          displayLoader(btn, 6000);
        }
        try {
          await api.navigateToCheckout({
            planId: plan.id!,
            checkoutFlowId,
            widgetType,
          });
        } catch (e) {
          // TODO: Add error handling
        }
      }),
    );
  };

  return {
    pageReady() {
      if (!flowAPI.environment.isEditor) {
        /*
          Collapse image on initializing, so default image
          doesn't show up before image from plan data is set
        */
        $w(`#${PlanWidgetRole.Image}`).collapse();
      }
    },
    exports: {
      setPlan: async (plan: PublicPlan | PlanV3, elementOptions?: PlanElementOptions) => {
        updatePlanData(plan, elementOptions);
      },
      onSelect: (cb: CtaClickHandler) => {
        externalOnSelectHandler = cb;
      },
      registerCtaHandler: async (plan: PublicPlan | PlanV3, widgetType: string, isOnEcom: boolean) => {
        return registerCtaHandler(plan, widgetType, isOnEcom);
      },
    },
  };
});

const DEFAULT_IMAGE_SRC =
  'wix:image://v1/11062b_916e6674cff64736acbf49ae13678e99~mv2.jpeg/Purple%20Podiums.jpeg#originWidth=6000&originHeight=2725';

function planImageToCoverImage(image: Image | undefined): CoverImage | undefined {
  const { id, width, height } = image ?? {};
  return id && width && height ? { uri: id, width, height } : undefined;
}

function getImageSrc(coverImage?: CoverImage): string {
  if (coverImage) {
    const { uri, width, height } = coverImage;
    return `wix:image://v1/${uri}/${uri}#originWidth=${width}&originHeight=${height}`;
  }

  return DEFAULT_IMAGE_SRC;
}
